import { useMemo } from "react";

import { useFullTextAssetSearchInfinite } from "~/v2/api/hooks/useGetAssetsFullTextSearch";
import type { AssetSearchResponse } from "~/v2/api/rest";
import type { AssetSearchTab } from "./AssetSearch.types";

/** Asset classes shown in search results. */
const ALLOWED_ASSET_CLASSES = ["us_equity", "crypto"] as const;

const TAB_CONFIG = [
  { id: "all", label: "All", assetClass: undefined },
  { id: "equities", label: "Equities", assetClass: "us_equity" },
  { id: "crypto", label: "Crypto", assetClass: "crypto" },
] as const;

export type AssetSearchTabId = AssetSearchTab["id"];
export type AssetSearchResult = AssetSearchResponse["assets"][number];
export type AssetSearchInfiniteQuery = ReturnType<
  typeof useFullTextAssetSearchInfinite
>;

export interface UseAssetSearchQueriesInput {
  query: string;
  activeTab: AssetSearchTabId;
  showFilters: boolean;
}

export interface AssetSearchQueriesValue {
  allQuery: AssetSearchInfiniteQuery;
  equitiesQuery: AssetSearchInfiniteQuery;
  cryptoQuery: AssetSearchInfiniteQuery;
  currentQuery: AssetSearchInfiniteQuery;
  activeAssets: AssetSearchResult[];
  tabs: readonly AssetSearchTab[];
}

export const useAssetSearchQueries = ({
  query,
  activeTab,
  showFilters,
}: UseAssetSearchQueriesInput): AssetSearchQueriesValue => {
  const allQuery = useFullTextAssetSearchInfinite({
    query,
    assetClass: undefined,
    exchanges: undefined,
  });

  const equitiesQuery = useFullTextAssetSearchInfinite({
    query,
    assetClass: "us_equity",
    exchanges: undefined,
    enabled: showFilters,
  });

  const cryptoQuery = useFullTextAssetSearchInfinite({
    query,
    assetClass: "crypto",
    exchanges: undefined,
    enabled: showFilters,
  });

  const queries = useMemo(
    () => ({ all: allQuery, equities: equitiesQuery, crypto: cryptoQuery }),
    [allQuery, equitiesQuery, cryptoQuery],
  );

  const currentQuery = queries[activeTab];
  const activeAssets = useMemo(
    () =>
      (
        currentQuery.data?.pages.flatMap((page) =>
          Array.isArray(page) ? page : (page.assets ?? []),
        ) ?? []
      ).filter((asset) =>
        (ALLOWED_ASSET_CLASSES as readonly string[]).includes(asset.class),
      ),
    [currentQuery.data],
  );

  const tabs = useMemo(
    () =>
      TAB_CONFIG.map((t) => {
        if (!showFilters) {
          return { id: t.id, label: t.label, count: 0 };
        }
        if (t.id === "all") {
          const equitiesFirstPage = equitiesQuery.data?.pages[0];
          const cryptoFirstPage = cryptoQuery.data?.pages[0];
          if (!equitiesFirstPage || !cryptoFirstPage) {
            return { id: t.id, label: t.label, count: 0 };
          }
          return {
            id: t.id,
            label: t.label,
            count:
              (equitiesFirstPage.count ?? 0) + (cryptoFirstPage.count ?? 0),
          };
        }
        const firstPage = queries[t.id as "equities" | "crypto"].data?.pages[0];
        return { id: t.id, label: t.label, count: firstPage?.count ?? 0 };
      }) as readonly AssetSearchTab[],
    [showFilters, equitiesQuery.data, cryptoQuery.data, queries],
  );

  return {
    allQuery,
    equitiesQuery,
    cryptoQuery,
    currentQuery,
    activeAssets,
    tabs,
  };
};
